import { Context, createContext, useEffect, useState } from "react";
import {
  App,
  arrayBufferToBase64,
  fullEndpoints,
  pemToArrayBuffer,
} from "@songtradr-sso/shared/common";
import { errorStaticText } from "../../statics/error-text";
import { createAxiosClient } from "../../utils/api.utils";

// TEXT

// STYLES

// DATA
export const getRsaPublicKey = async () => {
  const Api = createAxiosClient();
  return await Api.get(fullEndpoints[App.Core].rsa.publicKey);
};

// TYPES
export type RsaContextType = {
  rsaEncrypt: (value: string) => Promise<string>;
};

// CONSTANTS
export const RsaContext: Context<RsaContextType> =
  createContext<RsaContextType>({
    rsaEncrypt: () => Promise.resolve(""),
  });

// FUNCTIONS
export const useCryptoKey = () => {
  const [cryptoKey, setCryptoKey] = useState<CryptoKey>();

  useEffect(() => {
    const fetchRsaKey = async () => {
      try {
        const response = await getRsaPublicKey();
        const fetchedPublicKey = response.data.publicKey;

        // Convert PEM to ArrayBuffer
        const keyBuffer = pemToArrayBuffer(fetchedPublicKey);

        // Import the public key using Web Crypto API with RSA-OAEP and SHA-256
        const importedKey = await window.crypto.subtle.importKey(
          "spki",
          keyBuffer,
          {
            name: "RSA-OAEP",
            hash: "SHA-256",
          },
          true,
          ["encrypt"],
        );

        setCryptoKey(importedKey);
      } catch (error) {
        console.error("Error fetching RSA public key:", error);
        throw new Error(errorStaticText["Rsa.KeyFetchFailed"]);
      }
    };

    void fetchRsaKey();
  }, []);

  return cryptoKey;
};

export const encrypt = async (value: string, key?: CryptoKey) => {
  if (!key) throw new Error(errorStaticText["Rsa.EncryptionKeyMissing"]);
  if (value.length > 446) throw new Error(errorStaticText["Rsa.InputTooLong"]);

  try {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(value);
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: "RSA-OAEP",
      },
      key,
      encoded,
    );
    const encryptedBase64 = arrayBufferToBase64(encryptedBuffer);
    if (!encryptedBase64) throw new Error();

    return encryptedBase64;
  } catch (error) {
    console.error("Error encrypting value:", error);
    throw new Error(errorStaticText["Rsa.EncryptionFailed"]);
  }
};

// ASSETS
